// frontend/src/components/MasterGrid.jsx

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useSortBy,
  usePagination
} from 'react-table';

// 1) Columns to hide
const DISPLAY_EXCLUDED = [  
    'id',
    'label',
    'stage_id',
    'created_at',
    'project_id',
    'updated_at',
    'customer_id',
    'customer_city',
    'customer_email',
    'customer_phone',
    'customer_state',
    'project_number',
    'customer_mobile',
    'customer_address',
    'customer_country',
    'customer_zipcode',
    'customer_last_name',
    'customer_first_name',
    'parent_ticket_number',
    'customer_country_code'
   ];

// 2) Header remapping
const HEADER_MAP = { 
    avatar_url:         'Picture',
    ticket_number:      'ID',
    name:               'Job Title',
    status:             'Status',
    booking_end:        'Due Date',
    reason:             'Colors',
    customer_full_name: 'Customer Full Name',
    money_total:        'Value'
   };

// 1a) Statuses we never want to show in the master grid
const STATUS_EXCLUDED = [
      'Packaged / Done',
      'Net 30',
      'Chats',
      'Pickup Up - Not Paid',
      'Sent Invoice',
      'Paid Invoice'
    ];

// 3) Fallback header
function defaultHeader(key) {
  return key
    .split('_')
    .map(w => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

// 4) Per-column filter UI
function DefaultColumnFilter({ column: { filterValue, setFilter, Header } }) {
  return (
    <input
      value={filterValue || ''}
      onChange={e => setFilter(e.target.value || undefined)}
      placeholder={`Filter ${Header}`}
      style={{ width: '100%' }}
    />
  );
}

export default function MasterGrid({ uploadId, onToggle }) {
  const [data, setData]       = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [globalFilter, setGlobalFilter] = useState('');

  // 5) Load jobs and mark which are in today's list
  useEffect(() => {
      axios.get(`/api/uploads/${uploadId}/jobs/`)
        .then(res => {
          // filter out unwanted statuses
          const filtered = res.data.filter(
            job => !STATUS_EXCLUDED.includes(job.status)
          );
  
          setData(filtered);
  
          // also recompute which ones are already in the daily list
          const inDaily = new Set(
            filtered
              .filter(job => job.__in_daily)
              .map(job => job.__job_pk)
          );
          setSelected(inDaily);
        })
        .catch(() => setData([]));
    }, [uploadId]);

  // 6) Live add/remove toggle
  const toggle = useCallback((pk) => {
    const willRemove = selected.has(pk);
    const endpoint   = willRemove ? 'remove_selected' : 'push_selected';

    axios.post(
      `/api/uploads/${uploadId}/jobs/${endpoint}/`,
      { job_ids: [pk] }
    ).then(() => {
      const s = new Set(selected);
      willRemove ? s.delete(pk) : s.add(pk);
      setSelected(s);
      if (onToggle) onToggle();
    }).catch(err => {
      console.error(`Failed to ${endpoint}:`, err);
      alert('Error syncing selection');
    });
  }, [selected, uploadId, onToggle]);

  // 7) Build columns: all dataCols + one Actions column
  const columns = useMemo(() => {
    // dataCols
    const dataCols = data.length
      ? Object.keys(data[0])
          .filter(
            key => !key.startsWith('__') && !DISPLAY_EXCLUDED.includes(key)
          )
          .map(key => {
            // Avatar
            if (key === 'avatar_url') {
              return {
                Header: HEADER_MAP[key] || defaultHeader(key),
                accessor: key,
                Filter: DefaultColumnFilter,
                Cell: ({ value }) =>
                  value ? <img src={value} alt="" style={{ width:50, height:50 }} /> : null
              };
            }
            // Dates
            if (['created_at','updated_at','booking_end'].includes(key)) {
              return {
                Header: HEADER_MAP[key] || defaultHeader(key),
                accessor: key,
                Filter: DefaultColumnFilter,
                Cell: ({ value }) => {
                  if (!value) return '';
                  const d = new Date(value);
                  let s = d.toLocaleDateString('en-US',{
                    month:'short',day:'numeric',year:'numeric'
                  });
                  return s.replace(/^([A-Za-z]{3}) /,'$1. ');
                }
              };
            }
            // Money
            if (key === 'money_total') {
              return {
                Header: HEADER_MAP[key] || defaultHeader(key),
                accessor: key,
                Filter: DefaultColumnFilter,
                Cell: ({ value }) => {
                  if (value==null) return '';
                  return Number(value).toLocaleString('en-US',{
                    style:'currency',currency:'USD',minimumFractionDigits:2
                  });
                }
              };
            }
            // Default
            return {
              Header: HEADER_MAP[key] || defaultHeader(key),
              accessor: key,
              Filter: DefaultColumnFilter
            };
          })
      : [];

    // Actions column
    const actionCol = {
      Header: 'Actions',
      accessor: '__job_pk',
      disableSortBy: true,
      Filter: () => null,
      Cell: ({ value }) => (
        <button onClick={() => toggle(value)}>
          {selected.has(value) ? 'Remove' : 'Add'}
        </button>
      )
    };

    return [...dataCols, actionCol];
  }, [data, selected, toggle]);

  // 8) Table setup
  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state: { pageIndex },
    nextPage,
    previousPage,
    setGlobalFilter: applyFilter
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: { pageIndex: 0, globalFilter }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

useEffect(() => {
     applyFilter(globalFilter);
}, [globalFilter, applyFilter]);

  // 9) Render
  return (
    <div>
      <div style={{ marginBottom:10 }}>
        <input
          value={globalFilter}
          onChange={e=>setGlobalFilter(e.target.value)}
          placeholder="Search all columns..."
          style={{ padding:'4px 8px' }}
        />
      </div>

      <table {...getTableProps()} style={{ width:'100%',borderCollapse:'collapse' }}>
        <thead>
        {headerGroups.map(hg => {
            // pull `key` out so it isn't included in the spread
            const { key: _hgKey, ...hgProps } = hg.getHeaderGroupProps();
            return (
                <React.Fragment key={hg.id} {...hgProps}>
                    <tr>
                        {hg.headers.map(col => {
                            // same for each column header
                            const {
                                key: _colKey,
                                ...colProps
                            } = col.getHeaderProps(col.getSortByToggleProps());
                            return (
                                <th
                                    key={col.id}
                                    {...colProps}
                                    style={{
                                        borderBottom: '2px solid #aaa',
                                        padding: '8px',
                                        textAlign: 'left'
                                    }}
                                >
                                    {col.render('Header')}
                                    {col.isSorted
                                        ? col.isSortedDesc
                                            ? ' 🔽'
                                            : ' 🔼'
                                        : ''}
                                    </th>
                                );
                            })}
                        </tr>
                        <tr>
                            {hg.headers.map(col => {
                                // and for each filter cell, if you’re spreading props there:
                                const { key: _fKey, ...filterProps } = col.getHeaderProps();
                                return (
                                    <th
                                        key={`${col.id}-filter`}
                                        {...filterProps}
                                        style={{ padding: '4px' }}
                                    >
                                        {col.canFilter ? col.render('Filter') : null}
                                    </th>
                                );
                            })}
                        </tr>
                    </React.Fragment>
                );
            })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row=>{
            prepareRow(row);
            return (
              <tr key={row.id} {...row.getRowProps()}>
                {row.cells.map(cell=>(
                  <td
                    key={cell.column.id}
                    {...cell.getCellProps()}
                    style={{ padding:'8px',borderBottom:'1px solid #ddd' }}
                  >
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ marginTop:10 }}>
        <button onClick={previousPage} disabled={!canPreviousPage}>
          Previous
        </button>{' '}
        <span>
          Page <strong>{pageIndex+1} of {pageOptions.length}</strong>
        </span>{' '}
        <button onClick={nextPage} disabled={!canNextPage}>
          Next
        </button>
      </div>
    </div>
  );
}
